import React, { useState, useEffect } from 'react';
import { hrmAPI } from '../../lib/api';
import { Plus, Edit, Trash2, Search, RefreshCw } from 'lucide-react';

const SalaryStructures = () => {
    const [structures, setStructures] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    
    const [formData, setFormData] = useState({
        employee_id: '',
        name: '',
        gross_salary: '',
        basic_percentage: 60,
        house_rent_percentage: 30,
        medical_percentage: 10,
        conveyance_allowance: '',
        special_allowance: '',
        other_allowance: '',
        transport_allowance: '',
        mobile_allowance: '',
        tax_deduction: '',
        provident_fund: '',
        other_deductions: '',
        is_active: true
    });

    useEffect(() => { fetchData(); }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [strRes, empRes] = await Promise.all([
                hrmAPI.getSalaryStructures(),
                hrmAPI.getEmployees()
            ]);
            setStructures(strRes.data.data || strRes.data);
            setEmployees(empRes.data.data || empRes.data);
        } catch (err) { setError('Failed to load data'); }
        finally { setLoading(false); }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingId) {
                await hrmAPI.updateSalaryStructure(editingId, formData);
            } else {
                await hrmAPI.createSalaryStructure(formData);
            }
            setShowModal(false);
            resetForm();
            fetchData();
        } catch (err) { setError(err.response?.data?.message || 'Failed to save'); }
    };

    const handleEdit = (str) => {
        setFormData({
            employee_id: str.employee_id || '',
            name: str.name || '',
            gross_salary: str.gross_salary || '',
            basic_percentage: str.basic_percentage || 60,
            house_rent_percentage: str.house_rent_percentage || 30,
            medical_percentage: str.medical_percentage || 10,
            conveyance_allowance: str.conveyance_allowance || '',
            special_allowance: str.special_allowance || '',
            other_allowance: str.other_allowance || '',
            transport_allowance: str.transport_allowance || '',
            mobile_allowance: str.mobile_allowance || '',
            tax_deduction: str.tax_deduction || '',
            provident_fund: str.provident_fund || '',
            other_deductions: str.other_deductions || '',
            is_active: str.is_active ?? true
        });
        setEditingId(str.id);
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure?')) return;
        try {
            await hrmAPI.deleteSalaryStructure(id);
            fetchData();
        } catch (err) { setError('Failed to delete'); }
    };

    const resetForm = () => {
        setFormData({
            employee_id: '',
            name: '',
            gross_salary: '',
            basic_percentage: 60,
            house_rent_percentage: 30,
            medical_percentage: 10,
            conveyance_allowance: '',
            special_allowance: '',
            other_allowance: '',
            transport_allowance: '',
            mobile_allowance: '',
            tax_deduction: '',
            provident_fund: '',
            other_deductions: '',
            is_active: true
        });
        setEditingId(null);
    };

    const getEmployeeName = (id) => employees.find(e => e.id === id)?.name || '-';
    
    // Calculate from percentages
    const calculateFromGross = (gross, percentage) => {
        if (!gross || !percentage) return 0;
        return (parseFloat(gross) * parseFloat(percentage) / 100).toFixed(2);
    };

    // Calculate Gross (Basic + House Rent + Medical + Conveyance + Special + Other)
    const calculateGross = (str) => {
        const basic = parseFloat(str.basic_salary || 0);
        const houseRent = parseFloat(str.house_rent || 0);
        const medical = parseFloat(str.medical_allowance || 0);
        const conveyance = parseFloat(str.conveyance_allowance || 0);
        const special = parseFloat(str.special_allowance || 0);
        const other = parseFloat(str.other_allowance || 0);
        return (basic + houseRent + medical + conveyance + special + other).toFixed(2);
    };

    // Calculate Net = Gross - Deductions
    const calculateNet = (str) => {
        const gross = parseFloat(calculateGross(str));
        const tax = parseFloat(str.tax_deduction || 0);
        const pf = parseFloat(str.provident_fund || 0);
        const other = parseFloat(str.other_deductions || 0);
        return (gross - tax - pf - other).toFixed(2);
    };

    // Total cost to company (Gross + Transport + Mobile)
    const calculateTotalCost = (str) => {
        const gross = parseFloat(calculateGross(str));
        const transport = parseFloat(str.transport_allowance || 0);
        const mobile = parseFloat(str.mobile_allowance || 0);
        return (gross + transport + mobile).toFixed(2);
    };

    const filteredStructures = structures.filter(str => getEmployeeName(str.employee_id).toLowerCase().includes(searchTerm.toLowerCase()));

    return (
        <div>
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">Salary Structures</h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Manage employee salary structures</p>
                </div>
                <button onClick={() => { resetForm(); setShowModal(true); }} className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg">
                    <Plus className="w-4 h-4 mr-2" /> Add Structure
                </button>
            </div>

            {error && <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">{error}</div>}

            <div className="mb-4 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input type="text" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-800 dark:text-gray-200" />
            </div>

            <div className="bg-white dark:bg-slate-700 rounded-lg shadow overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center"><RefreshCw className="w-8 h-8 animate-spin mx-auto text-indigo-600" /></div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 dark:bg-slate-600">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Employee</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Gross Salary</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Net Salary</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Total Cost</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-slate-600">
                                {filteredStructures.length > 0 ? filteredStructures.map(str => (
                                    <tr key={str.id} className="hover:bg-gray-50 dark:hover:bg-slate-600">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800 dark:text-gray-200">{getEmployeeName(str.employee_id)}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{calculateGross(str)}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300 font-medium">{calculateNet(str)}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{calculateTotalCost(str)}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                                            <button onClick={() => handleEdit(str)} className="text-indigo-600 hover:text-indigo-900 mr-3"><Edit className="w-4 h-4" /></button>
                                            <button onClick={() => handleDelete(str.id)} className="text-red-600 hover:text-red-900"><Trash2 className="w-4 h-4" /></button>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan="5" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">No records found</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-slate-700 rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
                        <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-100">{editingId ? 'Edit' : 'Add'} Salary Structure</h2>
                        <form onSubmit={handleSubmit}>
                            <div className="mb-3">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Employee</label>
                                <select value={formData.employee_id} onChange={(e) => setFormData({...formData, employee_id: e.target.value})} className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" required>
                                    <option value="">Select Employee</option>
                                    {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                                </select>
                            </div>

                            <div className="mb-3">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Structure Name</label>
                                <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} 
                                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" 
                                    placeholder="e.g., Grade A - 2024" />
                            </div>
                            
                            {/* Gross Salary Section */}
                            <div className="bg-indigo-50 dark:bg-slate-800 p-3 rounded-lg mb-4">
                                <h3 className="text-sm font-semibold text-indigo-700 dark:text-indigo-300 mb-3">Gross Salary Configuration</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="mb-3">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Gross Salary</label>
                                        <input type="number" value={formData.gross_salary} onChange={(e) => setFormData({...formData, gross_salary: e.target.value})} 
                                            className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" required />
                                    </div>
                                </div>
                                <div className="grid grid-cols-3 gap-4">
                                    <div className="mb-3">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Basic (%)</label>
                                        <input type="number" value={formData.basic_percentage} onChange={(e) => setFormData({...formData, basic_percentage: e.target.value})} 
                                            className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" />
                                        <span className="text-xs text-gray-500">= {calculateFromGross(formData.gross_salary, formData.basic_percentage)}</span>
                                    </div>
                                    <div className="mb-3">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">House Rent (%)</label>
                                        <input type="number" value={formData.house_rent_percentage} onChange={(e) => setFormData({...formData, house_rent_percentage: e.target.value})} 
                                            className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" />
                                        <span className="text-xs text-gray-500">= {calculateFromGross(formData.gross_salary, formData.house_rent_percentage)}</span>
                                    </div>
                                    <div className="mb-3">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Medical (%)</label>
                                        <input type="number" value={formData.medical_percentage} onChange={(e) => setFormData({...formData, medical_percentage: e.target.value})} 
                                            className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" />
                                        <span className="text-xs text-gray-500">= {calculateFromGross(formData.gross_salary, formData.medical_percentage)}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Allowances (part of Gross) */}
                            <div className="bg-green-50 dark:bg-slate-800 p-3 rounded-lg mb-4">
                                <h3 className="text-sm font-semibold text-green-700 dark:text-green-300 mb-3">Allowances (Included in Gross)</h3>
                                <div className="grid grid-cols-3 gap-4">
                                    <div className="mb-3">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Conveyance</label>
                                        <input type="number" value={formData.conveyance_allowance} onChange={(e) => setFormData({...formData, conveyance_allowance: e.target.value})} 
                                            className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" />
                                    </div>
                                    <div className="mb-3">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Special</label>
                                        <input type="number" value={formData.special_allowance} onChange={(e) => setFormData({...formData, special_allowance: e.target.value})} 
                                            className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" />
                                    </div>
                                    <div className="mb-3">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Other</label>
                                        <input type="number" value={formData.other_allowance} onChange={(e) => setFormData({...formData, other_allowance: e.target.value})} 
                                            className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" />
                                    </div>
                                </div>
                            </div>

                            {/* Separate Payments (Not in Gross) */}
                            <div className="bg-yellow-50 dark:bg-slate-800 p-3 rounded-lg mb-4">
                                <h3 className="text-sm font-semibold text-yellow-700 dark:text-yellow-300 mb-3">Separate Payments (Not in Gross)</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="mb-3">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Transport Allowance</label>
                                        <input type="number" value={formData.transport_allowance} onChange={(e) => setFormData({...formData, transport_allowance: e.target.value})} 
                                            className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" />
                                    </div>
                                    <div className="mb-3">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mobile Allowance</label>
                                        <input type="number" value={formData.mobile_allowance} onChange={(e) => setFormData({...formData, mobile_allowance: e.target.value})} 
                                            className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" />
                                    </div>
                                </div>
                            </div>

                            {/* Deductions */}
                            <div className="bg-red-50 dark:bg-slate-800 p-3 rounded-lg mb-4">
                                <h3 className="text-sm font-semibold text-red-700 dark:text-red-300 mb-3">Deductions</h3>
                                <div className="grid grid-cols-3 gap-4">
                                    <div className="mb-3">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tax</label>
                                        <input type="number" value={formData.tax_deduction} onChange={(e) => setFormData({...formData, tax_deduction: e.target.value})} 
                                            className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" />
                                    </div>
                                    <div className="mb-3">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Provident Fund</label>
                                        <input type="number" value={formData.provident_fund} onChange={(e) => setFormData({...formData, provident_fund: e.target.value})} 
                                            className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" />
                                    </div>
                                    <div className="mb-3">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Other</label>
                                        <input type="number" value={formData.other_deductions} onChange={(e) => setFormData({...formData, other_deductions: e.target.value})} 
                                            className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" />
                                    </div>
                                </div>
                            </div>

                            <div className="mb-3">
                                <label className="flex items-center">
                                    <input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                                        className="mr-2" />
                                    <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
                                </label>
                            </div>
                            <div className="flex justify-end gap-3 mt-4">
                                <button type="button" onClick={() => { setShowModal(false); resetForm(); }} className="px-4 py-2 border rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50">Cancel</button>
                                <button type="submit" className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg">{editingId ? 'Update' : 'Create'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SalaryStructures;
